import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableVirtualScrollDataSource, TableVirtualScrollModule } from 'ng-table-virtual-scroll';
import { Subscriber } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  DEFAULT_SCANNER_TABLE_SORT,
  SELECT_SCANNER_SYMBOL_DEBOUNCE_TIME,
} from '@c/shared/scanner-symbols-list/scanner-symbols-list.data';
import { IScannerTableItem, SortState } from '@c/shared/scanner-symbols-list/scanner-symbols-list.model';
import { SymbolFlagModule } from '@c/shared/symbol-flag/symbol-flag.module';
import { ResizeDirectiveModule } from '@core/directives/resize-directive/resize-directive.module';
import { SymbolCasePipe } from '@core/pipes/symbol-case.pipe';
import { SortDirection } from '@core/types';
import { Flags, SmileyListType } from '@mod/symbol-smiley/symbol-smiley.model';
import { ObservableService as ObservableServiceV2 } from '@s/observable.service';
import { SmileyDataService } from '@s/smiley-data.service';
import { UserDataService } from '@s/user-data.service';
import { isOverlayOpen } from '@u/ui-utils';
import { transformShortNumberToFullNumber } from '@u/utils';

@Component({
  selector: 'app-scanner-symbols-list',
  templateUrl: './scanner-symbols-list.component.html',
  styleUrls: ['./scanner-symbols-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    TableVirtualScrollModule,
    ScrollingModule,
    ResizeDirectiveModule,
    MatTableModule,
    MatSortModule,
    SymbolFlagModule,
    SymbolCasePipe,
    NgClass,
    MatTooltipModule,
  ],
})
export class ScannerSymbolsListComponent<T extends IScannerTableItem = IScannerTableItem>
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input() saveSortStateKey: string | null = null;
  @Input() isActive: boolean;
  @Input() hideRemoveButton = true;
  @Input() data: T[] = [];
  @Input() smileyListType: SmileyListType | null = null;
  @Input() userSettingsSymbolKey: string;
  @Input() readonly sortHeaderTitles: Record<string, string> = {};

  @Input() set optionalDisplayedColumns(value: string[]) {
    this.additionalDisplayedColumns = value;
    this.allDisplayedColumns = [...this.basicDisplayedColumns, ...value];
  }

  @Input() set currentSymbol({ security_id }: { security_id: number }) {
    if (!this.isActive && this.selectedRow) {
      this.selectedRow = null;
    }
  }

  @Output() triggerSmiley: EventEmitter<{ security_id: number; flag: Flags }> = new EventEmitter(null);
  @Output() symbolSelected: EventEmitter<T> = new EventEmitter(null);
  @Output() symbolRemoved: EventEmitter<T> = new EventEmitter(null);

  @ViewChild(MatSort) sort: MatSort;

  protected readonly ITEM_HEIGHT_PX = 30;

  protected readonly smileyListTypes = SmileyListType;
  protected readonly flags = Flags;
  protected readonly defaultSortState: SortState = { ...DEFAULT_SCANNER_TABLE_SORT };

  protected dataSource = new TableVirtualScrollDataSource<T>([]);
  protected renderedData: T[] = [];

  protected columns = ['flag', 'symbol', 'company'];
  protected basicSortHeaderTitles: Record<string, string> = {
    flag: 'Flag',
    symbol: 'Symbol',
    company: 'Company',
  };

  protected sortState: SortState = { ...DEFAULT_SCANNER_TABLE_SORT };
  protected basicDisplayedColumns = ['flag', 'symbol', 'company'];
  protected additionalDisplayedColumns: string[] = [];
  protected allDisplayedColumns = ['flag', 'symbol', 'company'];

  protected selectedRow: T | null = null;
  protected timeoutUpDown: NodeJS.Timeout;

  protected isAbleToUpDown = true;
  protected isSmileyMenuOpened = false;
  protected showSmileyStatistic = false;

  protected readonly flagAscSortOrder = [Flags.Never, Flags.No, Flags.Maybe, Flags.Yes, Flags.None];
  protected subscriber = new Subscriber();

  private sortDirectionValue: SortDirection = 'asc';
  private prevSelectedColumn: string = this.sortState.column;

  constructor(
    private dialog: MatDialog,
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
    private observableServiceV2: ObservableServiceV2,
    private userDataService: UserDataService,
    private smileyDataService: SmileyDataService,
  ) {}

  ngOnInit(): void {
    // TODO: remove this, the component is reusable (not legacy, actively used), so it shouldn't have any specific logic
    // Use "app-scanner-symbols-list-v2" for scanner list with custom columns (as parameters, don't modify the component itself)

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    this.dataSource.sortingDataAccessor = (data, headerId) => {
      if (headerId === 'flag') {
        return this.flagAscSortOrder.indexOf(data.flag as Flags);
      }

      const isCleanNumber = !isNaN(Number(data[headerId]));

      const shortNumberRegExp = new RegExp(/\d+(\.\d+)?[KMBT]/g);
      const isShortFormatNumber = shortNumberRegExp.test(data[headerId]);

      // compare number-like strings
      if (typeof data[headerId] === 'string' && isCleanNumber) {
        return Number(data[headerId]);
      }

      // compare number-like strings (short format)
      if (typeof data[headerId] === 'string' && isShortFormatNumber) {
        return transformShortNumberToFullNumber(data[headerId]);
      }

      if (typeof data[headerId] === 'string') {
        return data[headerId].toLowerCase();
      }

      return data[headerId];
    };

    this.subscriber.add(
      this.dataSource.connect().subscribe((data) => {
        this.renderedData = data;
      }),
    );

    this.subscriber.add(
      this.smileyDataService.showStatisticSettingAndHasAccess$.pipe(distinctUntilChanged()).subscribe((value) => {
        this.showSmileyStatistic = value;
      }),
    );

    if (this.userSettingsSymbolKey && this.observableServiceV2[this.userSettingsSymbolKey]) {
      this.subscriber.add(
        this.observableServiceV2[this.userSettingsSymbolKey].subscribe(() => {
          if (!this.isActive && this.selectedRow) {
            this.selectedRow = null;
          }
        }),
      );
    }

    if (this.saveSortStateKey && this.observableServiceV2[this.saveSortStateKey]) {
      // restore sort settings, including updates published before they are saved
      this.subscriber.add(
        this.observableServiceV2[this.saveSortStateKey].subscribe((storedSortState) => {
          // TODO: validate "sortState" object - fields and values
          if (storedSortState) {
            this.applySortState(storedSortState);
          }
        }),
      );
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.subscriber.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.data && changes.data.currentValue && !this.isSmileyMenuOpened) {
      this.data = changes.data.currentValue;
      this.pushSortedData();
      this.changeDetectorRef.detectChanges();
    }

    if (changes && changes.isActive && changes.isActive.currentValue) {
      // for virtual-scroll - without this table is empty after change tab
      window.dispatchEvent(new Event('resize'));
    }

    this.changeDetectorRef.markForCheck();
  }

  public onResize(): void {
    window.dispatchEvent(new Event('resize'));
  }

  protected async onSortChange(sortState: { active: string; direction: SortDirection }): Promise<void> {
    if (!sortState.active || !sortState.direction) {
      return;
    }

    const isSameColumn = this.prevSelectedColumn === sortState.active;
    const newDirection = isSameColumn ? sortState.direction : this.sortDirectionValue;
    const nextState: SortState = { column: sortState.active, direction: newDirection };

    if (this.sortState.column === nextState.column && this.sortState.direction === nextState.direction) {
      return;
    }

    this.prevSelectedColumn = sortState.active;
    this.sortDirectionValue = newDirection;
    this.sortState = nextState;

    if (this.sort) {
      this.sort.active = nextState.column;
      this.sort.direction = newDirection;
    }

    this.changeDetectorRef.detectChanges();

    if (this.selectedRow) {
      setTimeout(() => this.scrollToSelectedRow());
      this.changeDetectorRef.markForCheck();
    }

    // publish before the settings request so a recreated list reads the new sort
    if (this.saveSortStateKey && this.observableServiceV2[this.saveSortStateKey]) {
      this.observableServiceV2[this.saveSortStateKey].next(this.sortState);
    }

    if (this.saveSortStateKey) {
      await this.userDataService.set(this.saveSortStateKey, this.sortState);
    }
  }

  private applySortState(storedSortState: SortState): void {
    if (
      !storedSortState?.column ||
      !storedSortState.direction ||
      (this.sortState.column === storedSortState.column && this.sortState.direction === storedSortState.direction)
    ) {
      return;
    }

    this.sortState = { ...storedSortState };
    this.sortDirectionValue = storedSortState.direction;
    this.prevSelectedColumn = storedSortState.column;
    this.pushSortedData();

    if (this.selectedRow) {
      setTimeout(() => this.scrollToSelectedRow());
    }

    this.changeDetectorRef.markForCheck();
  }

  private pushSortedData(): void {
    if (this.sort && this.sortState.column && this.sortState.direction) {
      this.sort.active = this.sortState.column;
      this.sort.direction = this.sortState.direction;
    }

    this.dataSource.data = this.data;
  }

  protected onSmileyMenuClosed(): void {
    this.pushSortedData();
  }

  protected onSelectSymbol(element: T): void {
    clearTimeout(this.timeoutUpDown);
    this.timeoutUpDown = setTimeout(async () => {
      this.symbolSelected.emit(element);
    }, SELECT_SCANNER_SYMBOL_DEBOUNCE_TIME);
  }

  protected onSelectSmiley(securityId: number, flag: Flags): void {
    this.triggerSmiley.emit({ security_id: securityId, flag });
    this.changeDetectorRef.markForCheck();
  }

  protected onRemove(element: T): void {
    this.symbolRemoved.emit(element);
  }

  protected trackByFn(index: number, item: { id?: string | number }): string | number {
    return item.id ?? index;
  }

  private scrollToSelectedRow(): void {
    if (this.selectedRow && document.getElementById(`scanner-tr-${this.selectedRow.security_id}`)) {
      this.renderer
        .selectRootElement('#scanner-tr-' + this.selectedRow.security_id, true)
        .scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      this.changeDetectorRef.markForCheck();
    }
  }

  private arrowDownEvent(): void {
    const data = this.renderedData;
    const index = data.findIndex((el) => el.security_id === this.selectedRow.security_id);

    if (index < data.length - 1) {
      this.selectedRow = data[index + 1];
      this.changeDetectorRef.markForCheck();

      this.onSelectSymbol(data[index + 1]);
      this.scrollToSelectedRow();
    }
  }

  private arrowUpEvent(): void {
    const data = this.renderedData;
    const index = data.findIndex((el) => el.security_id === this.selectedRow.security_id);

    if (index > 0) {
      this.selectedRow = data[index - 1];
      this.onSelectSymbol(data[index - 1]);
      this.scrollToSelectedRow();
    }
  }

  // TODO: remove this
  // No getters and function calls in templates
  // For this component all values should be prepared in parent component
  protected getEarningsInfoClass(status: string): string {
    switch (status) {
      case 'BMO':
        return 'before-market';
      case 'AMC':
        return 'after-market';
      case 'DMH':
        return 'during-market';
      default:
        return '';
    }
  }

  // TODO: create service that takes opened dialogs, overlays, focused inputs, etc
  // and return observable that emits filtered up/down keys events
  // or up/down + additional details: focused inputs, opened dialogs, etc
  // return several observables for different cases, foe example navigate-lists up/down
  // could include on-key-hold functionality

  // TODO: implement on-key-hold functionality: when user hold arrow key move selection to next symbol every [DEFINED_TIME_INTERVAL]
  @HostListener('window:keydown', ['$event'])
  onHostKeyDown(event: KeyboardEvent): void {
    const { code } = event;
    const dialogOpen = this.dialog.openDialogs.length;

    if (
      this.isActive &&
      this.isAbleToUpDown &&
      !isOverlayOpen() &&
      dialogOpen === 0 &&
      ['ArrowUp', 'ArrowDown'].includes(code)
    ) {
      event.preventDefault();

      if (!this.selectedRow) {
        const firstEl = this.renderedData[0];
        this.onSelectSymbol(firstEl);
        this.selectedRow = firstEl;
        this.scrollToSelectedRow();
        return;
      }

      if (code === 'ArrowUp') {
        this.arrowUpEvent();
      } else if (code === 'ArrowDown') {
        this.arrowDownEvent();
      }
    }
  }
}
